import { useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, Button, Alert, Grid2 as Grid, FormControlLabel, Switch, Divider, Chip,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxShareResultDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ImageViewer } from "./ImageViewer";
import { formatDateTime } from "@/shared/datetime";

export function DiagnosticsPacsPage() {
  const canManage = useAuth(s => s.hasPermission("diagnostics.pacs.manage"));
  const qc = useQueryClient();
  const [studyId, setStudyId] = useState(""); const [active, setActive] = useState("");
  const [compare, setCompare] = useState(false);
  const [share, setShare] = useState<DxShareResultDto | null>(null);

  const images = useQuery({ queryKey: ["dx-pacs-images", active], queryFn: () => diagnosticsApi.listStudyImages(active), enabled: !!active });
  const refresh = () => qc.invalidateQueries({ queryKey: ["dx-pacs-images", active] });

  // register a single instance
  const [sop, setSop] = useState(""); const [url, setUrl] = useState(""); const [series, setSeries] = useState(""); const [inst, setInst] = useState(""); const [modality, setModality] = useState(""); const [desc, setDesc] = useState("");
  const register = useMutation({
    meta: { successMessage: "Image registered" },
    mutationFn: () => diagnosticsApi.registerPacsInstance({ studyId: active, sopInstanceUid: sop.trim(), imageUrl: url.trim(), seriesInstanceUid: series || null, instanceNumber: inst === "" ? null : Number(inst), modality: modality || null, description: desc || null }),
    onSuccess: () => { setSop(""); setUrl(""); setSeries(""); setInst(""); setDesc(""); refresh(); },
  });
  const importFromPacs = useMutation({ meta: { successMessage: "Imported from PACS" }, mutationFn: () => diagnosticsApi.importStudyImages(active), onSuccess: refresh });
  const [shareRecipient, setShareRecipient] = useState("");
  const doShare = useMutation({ meta: { successMessage: "Share link created" }, mutationFn: () => diagnosticsApi.shareStudy(active, { recipient: shareRecipient || null, ttlHours: 72 }), onSuccess: (d) => setShare(d) });
  const [hl7ReportId, setHl7ReportId] = useState("");
  const sendHl7 = useMutation({ meta: { successMessage: "HL7 (ORU) sent" }, mutationFn: () => diagnosticsApi.sendReportHl7(hl7ReportId) });

  const imgs = images.data ?? [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · PACS / Imaging</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField label="Study Id" size="small" value={studyId} onChange={e => setStudyId(e.target.value)} sx={{ flexGrow: 1 }} />
          <Button variant="outlined" disabled={!studyId} onClick={() => { setActive(studyId); setShare(null); }}>Load images</Button>
          {canManage && active && <Button variant="outlined" disabled={importFromPacs.isPending} onClick={() => importFromPacs.mutate()}>Import from PACS</Button>}
          {active && <FormControlLabel control={<Switch checked={compare} onChange={e => setCompare(e.target.checked)} />} label="Compare" />}
        </Stack>
        {images.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(images.error)}</Alert>}
        {importFromPacs.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(importFromPacs.error)}</Alert>}
        {active && <Typography variant="caption" color="text.secondary">{imgs.length} image(s)</Typography>}
      </Paper>

      {active && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: compare ? 6 : 12 }}>
            <Paper sx={{ p: 2 }}><ImageViewer images={imgs} label={compare ? "Series A" : undefined} /></Paper>
          </Grid>
          {compare && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}><ImageViewer images={imgs} label="Series B" /></Paper>
            </Grid>
          )}
        </Grid>
      )}

      {canManage && active && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Register image reference</Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                  <TextField label="SOP Instance UID" size="small" value={sop} onChange={e => setSop(e.target.value)} sx={{ flexGrow: 1 }} />
                  <TextField label="Series UID" size="small" value={series} onChange={e => setSeries(e.target.value)} sx={{ width: 150 }} />
                  <TextField label="Inst #" size="small" type="number" value={inst} onChange={e => setInst(e.target.value)} sx={{ width: 80 }} />
                </Stack>
                <TextField label="Rendered image URL" size="small" value={url} onChange={e => setUrl(e.target.value)} />
                <Stack direction="row" spacing={1}>
                  <TextField label="Modality" size="small" value={modality} onChange={e => setModality(e.target.value)} sx={{ width: 120 }} />
                  <TextField label="Description" size="small" value={desc} onChange={e => setDesc(e.target.value)} sx={{ flexGrow: 1 }} />
                </Stack>
                {register.isError && <Alert severity="error">{errorMessage(register.error)}</Alert>}
                <Button variant="contained" disabled={!sop.trim() || !url.trim() || register.isPending} onClick={() => register.mutate()} sx={{ alignSelf: "flex-start" }}>Register</Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Share study</Typography>
              <Stack spacing={1}>
                <TextField label="Recipient (optional)" size="small" value={shareRecipient} onChange={e => setShareRecipient(e.target.value)} />
                <Button variant="outlined" disabled={doShare.isPending} onClick={() => doShare.mutate()} sx={{ alignSelf: "flex-start" }}>Create share link (72h)</Button>
                {doShare.isError && <Alert severity="error">{errorMessage(doShare.error)}</Alert>}
                {share && (
                  <>
                    <Divider />
                    <Chip size="small" color="success" label="Link created" />
                    <TextField size="small" value={share.shareUrl} InputProps={{ readOnly: true }} onFocus={e => e.target.select()} />
                    {share.expiresAt && <Typography variant="caption" color="text.secondary">Expires {formatDateTime(share.expiresAt)}</Typography>}
                  </>
                )}
                <Divider />
                <Typography variant="subtitle2">Send HL7 result (ORU)</Typography>
                <Stack direction="row" spacing={1}>
                  <TextField label="Report Id" size="small" value={hl7ReportId} onChange={e => setHl7ReportId(e.target.value)} sx={{ flexGrow: 1 }} />
                  <Button variant="outlined" disabled={!hl7ReportId || sendHl7.isPending} onClick={() => sendHl7.mutate()}>Send HL7</Button>
                </Stack>
                {sendHl7.isError && <Alert severity="error">{errorMessage(sendHl7.error)}</Alert>}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
